import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { HiTrash, HiUpload, HiPlus, HiClipboardCopy } from 'react-icons/hi';
import { AppPageShell, AppLoadingState, Button, EmptyState, PageHeader } from './common/ui';

const KnowledgeBasePage = () => {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    text: ''
  });
  const [uploadFormData, setUploadFormData] = useState({
    name: '',
    description: ''
  });

  const fetchKnowledgeBases = async () => {
    try {
      setLoading(true);
      const response = await api.getKnowledgeBases();
      setKnowledgeBases(response.data.vectors || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  const handleCreateKnowledgeBase = async () => {
    try {
      setLoading(true);
      await api.createKnowledgeBase(formData);
      await fetchKnowledgeBases();
      setOpenCreateDialog(false);
      setFormData({ name: '', description: '', text: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('file', selectedFile);
    formDataToSend.append('name', uploadFormData.name || selectedFile.name);
    formDataToSend.append('description', uploadFormData.description || '');

    try {
      setError(null);
      setLoading(true);
      
      console.log('Uploading file:', {
        name: uploadFormData.name || selectedFile.name,
        description: uploadFormData.description,
        fileSize: selectedFile.size,
        fileType: selectedFile.type
      });

      if (selectedFile.type.includes('text') || selectedFile.name.endsWith('.txt')) {
        await api.uploadKnowledgeBaseText(formDataToSend);
      } else {
        await api.uploadKnowledgeBaseMedia(formDataToSend);
      }
      
      await fetchKnowledgeBases();
      setOpenUploadDialog(false);
      setSelectedFile(null);
      setUploadFormData({ name: '', description: '' });
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKnowledgeBase = async (vectorId) => {
    if (window.confirm('Are you sure you want to delete this knowledge base?')) {
      try {
        setLoading(true);
        await api.deleteKnowledgeBase(vectorId);
        await fetchKnowledgeBases();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
  };

  return (
    <AppPageShell maxWidthClass="max-w-7xl">
      <PageHeader
        eyebrow="Resources"
        title="Knowledge Bases"
        description="Create, upload, and manage reusable context sources for agent workflows."
        actions={(
          <>
            <Button
              onClick={() => setOpenCreateDialog(true)}
            >
              <HiPlus className="-ml-1 mr-2 h-5 w-5" />
              Create New
            </Button>
            <Button
              onClick={() => setOpenUploadDialog(true)}
              variant="secondary"
            >
              <HiUpload className="-ml-1 mr-2 h-5 w-5" />
              Upload File
            </Button>
          </>
        )}
      />

      {/* Error Alert */}
      {error && (
        <div className="fixed top-4 right-4 rounded-md bg-red-50 p-4 z-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <AppLoadingState
          blocking
          blockingTint="dim"
          withCard
          label="Processing your request…"
          description="Please do not close this page or refresh your browser."
        />
      ) : (
        /* Knowledge Bases List/Table */
        knowledgeBases.length === 0 ? (
          <EmptyState
            icon={<HiUpload className="h-5 w-5" />}
            title="No knowledge bases yet"
            description="Create a text source or upload a supported file to make agent context reusable."
            actions={(
              <>
                <Button onClick={() => setOpenCreateDialog(true)}>Create New</Button>
                <Button onClick={() => setOpenUploadDialog(true)} variant="secondary">Upload File</Button>
              </>
            )}
          />
        ) : (
        <>
          {/* Mobile View - Cards */}
          <div className="md:hidden space-y-4">
            {knowledgeBases.map((kb) => (
              <div key={kb.vector_id} className="content-panel p-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-900">{kb.name}</h3>
                    <p className="text-sm text-gray-500">{kb.description}</p>
                  </div>
                  <Button
                    onClick={() => handleDeleteKnowledgeBase(kb.vector_id)}
                    variant="danger"
                    size="icon"
                  >
                    <HiTrash className="h-5 w-5" />
                  </Button>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Vector ID:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900">{kb.vector_id.substring(0, 8)}...</span>
                      <Button
                        onClick={() => handleCopyId(kb.vector_id)}
                        variant="ghost"
                        size="icon"
                        title="Copy full ID"
                      >
                        <HiClipboardCopy className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Created:</span>
                    <span className="text-gray-900">{new Date(kb.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden md:flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div className="app-table">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vector ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created At
                        </th>
                        <th scope="col" className="relative px-6 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {knowledgeBases.map((kb) => (
                        <tr key={kb.vector_id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {kb.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {kb.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              <span>{kb.vector_id.substring(0, 12)}...</span>
                              <Button
                                onClick={() => handleCopyId(kb.vector_id)}
                                variant="ghost"
                                size="icon"
                                title="Copy full ID"
                              >
                                <HiClipboardCopy className="h-5 w-5" />
                              </Button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(kb.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              onClick={() => handleDeleteKnowledgeBase(kb.vector_id)}
                              variant="danger"
                              size="icon"
                            >
                              <HiTrash className="h-5 w-5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
        )
      )}

      {/* Create Dialog */}
      {openCreateDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setOpenCreateDialog(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => setOpenCreateDialog(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-3 sm:mt-0">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Create Knowledge Base</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                    <input
                      type="text"
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1 block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="text" className="block text-sm font-medium text-gray-700">Text Content</label>
                    <textarea
                      id="text"
                      rows={4}
                      value={formData.text}
                      onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                      className="mt-1 block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row-reverse gap-3">
                <Button
                  type="button"
                  onClick={handleCreateKnowledgeBase}
                  className="w-full sm:w-auto flex-1"
                >
                  Create
                </Button>
                <Button
                  type="button"
                  onClick={() => setOpenCreateDialog(false)}
                  className="w-full sm:w-auto flex-1"
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      {openUploadDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setOpenUploadDialog(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => setOpenUploadDialog(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-3 sm:mt-0">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Upload File</h3>
                <div className="space-y-5">
                  <div>
                    <label htmlFor="upload-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      id="upload-name"
                      value={uploadFormData.name}
                      onChange={(e) => setUploadFormData({ ...uploadFormData, name: e.target.value })}
                      className="block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                      placeholder="Enter file name"
                    />
                  </div>
                  <div>
                    <label htmlFor="upload-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      id="upload-description"
                      value={uploadFormData.description}
                      onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
                      className="block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                      placeholder="Enter description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                    <div 
                      className="flex flex-col items-center justify-center border-2 border-gray-300 border-dashed rounded-md py-6 px-4"
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file && file.type === 'audio/mpeg') {
                          setSelectedFile(file);
                          // Pre-fill name and description
                          setUploadFormData({
                            name: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
                            description: 'Voice Transcription'
                          });
                        } else {
                          setError('Only MP3 files are supported. Please contact support@Lifelike Agents for other file types.');
                        }
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <div className="text-center">
                        <HiUpload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <div className="flex items-center justify-center text-sm text-gray-600">
                          <label htmlFor="file-upload" className="relative cursor-pointer">
                            <span className="text-blue-600 hover:text-blue-500">Upload an MP3 file</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              accept=".mp3,audio/mpeg"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && file.type === 'audio/mpeg') {
                                  setSelectedFile(file);
                                  // Pre-fill name and description
                                  setUploadFormData({
                                    name: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
                                    description: 'Voice Transcription'
                                  });
                                } else {
                                  setError('Only MP3 files are supported. Please contact support@Lifelike Agents for other file types.');
                                  e.target.value = '';
                                }
                              }}
                              className="sr-only"
                            />
                          </label>
                          <span className="pl-1">or drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          MP3 files only. For other file types (PDF, DOC, TXT, etc.), please contact support@Lifelike Agents
                        </p>
                        {selectedFile && (
                          <div className="mt-4 text-sm text-gray-900">
                            <p className="font-medium">Selected: {selectedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 flex flex-row-reverse gap-3">
                <Button
                  type="button"
                  onClick={handleUploadFile}
                  disabled={!selectedFile}
                  className="flex-1"
                >
                  Upload
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setOpenUploadDialog(false);
                    setSelectedFile(null);
                    setUploadFormData({ name: '', description: '' });
                  }}
                  className="flex-1"
                  variant="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppPageShell>
  );
};

export default KnowledgeBasePage; 
